<script setup>
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useOperators } from 'dashboard/components-next/filter/operators.js';
import { useContactFilterContext } from 'dashboard/components-next/filter/contactProvider.js';
import { useCompanyFilterContext } from 'dashboard/components-next/filter/companyProvider.js';
import { useDealFilterContext } from 'dashboard/components-next/filter/dealProvider.js';
import { useContactLabelsStore } from 'dashboard/stores/contactLabels';
import { useCompanyLabelsStore } from 'dashboard/stores/companyLabels';
import { useDealLabelsStore } from 'dashboard/stores/dealLabels';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ConditionsEditor from './ConditionsEditor.vue';

// The include (or exclude) side of the audience form: one list of conditions
// across Contato/Empresa/Negociação. The conditions stay stored per entity
// (the Audience's criteria jsonb is per entity), so the list is rendered
// grouped by entity and the "add" menu picks the entity and field at once.
const props = defineProps({
  direction: {
    type: String,
    required: true,
    validator: value => ['include', 'exclude'].includes(value),
  },
  title: { type: String, required: true },
  description: { type: String, default: '' },
  showShortcuts: { type: Boolean, default: false },
});

const ENTITIES = ['contact', 'company', 'deal'];
const MENU_HEIGHT_PX = 240;

const { t } = useI18n();

const criteriaByEntity = defineModel({ type: Object, required: true });

const { containmentOperators, presenceOperators } = useOperators();
const contactFilterTypes = useContactFilterContext().filterTypes;

// Contact > Empresa is a legacy free-text attribute: the Empresa section filters the real company,
// so the field is only kept when a saved condition still uses it.
const usesLegacyCompanyName = () =>
  ['include', 'exclude'].some(direction =>
    criteriaByEntity.value.contact[direction].some(
      condition => condition.attributeKey === 'company_name'
    )
  );

// Operators audiences expose on top of the shared contact filter config:
// - name: the engine supports "contains", the contacts page only shows "equal to".
// - phone_number: audiences care about "has a phone / doesn't have a phone" to split
//   who actually receives the broadcast.
const presenceOnlyOperators = computed(() =>
  presenceOperators.value.filter(({ value }) =>
    ['is_present', 'is_not_present'].includes(value)
  )
);
const audienceOperatorsFor = type => {
  if (type.attributeKey === 'name') return containmentOperators.value;
  if (type.attributeKey === 'phone_number') {
    return [...type.filterOperators, ...presenceOnlyOperators.value];
  }
  return type.filterOperators;
};
const audienceContactFilterTypes = computed(() =>
  contactFilterTypes.value
    .filter(
      type => type.attributeKey !== 'company_name' || usesLegacyCompanyName()
    )
    .map(type => ({ ...type, filterOperators: audienceOperatorsFor(type) }))
);

const filterTypesByEntity = {
  contact: audienceContactFilterTypes,
  company: useCompanyFilterContext().filterTypes,
  deal: useDealFilterContext().filterTypes,
};

const conditionsOf = entity => criteriaByEntity.value[entity][props.direction];

// Saved criteria hold plain ids (['vip']); MultiSelect/SingleSelect work with
// the option objects ({ id, name }) and turn them back into ids on save. Ids
// with no matching option (e.g. a deleted label) are kept as they are.
const hydrateValues = () => {
  ENTITIES.forEach(entity => {
    conditionsOf(entity).forEach(condition => {
      const filter = filterTypesByEntity[entity].value.find(
        type => type.attributeKey === condition.attributeKey
      );
      const ids = [].concat(condition.values ?? []);
      const needsHydration =
        ids.length && ids.some(id => typeof id !== 'object');
      if (!needsHydration || !filter?.options?.length) return;

      const optionFor = id =>
        filter.options.find(option => String(option.id) === String(id)) ?? id;
      const operator = filter.filterOperators?.find(
        item => item.value === condition.filterOperator
      );
      const inputType = operator?.inputOverride ?? filter.inputType;
      if (inputType === 'multiSelect') {
        condition.values = ids.map(optionFor);
      } else if (inputType === 'searchSelect') {
        condition.values = optionFor(ids[0]);
      }
    });
  });
};

watch(
  () => [
    criteriaByEntity.value,
    ...ENTITIES.map(e => filterTypesByEntity[e].value),
  ],
  hydrateValues,
  { immediate: true, deep: true }
);

// One-click starting points for an empty list, for the fields people reach for most.
const shortcuts = computed(() =>
  [
    ['contact', 'labels', t('AUDIENCES.FORM.SHORTCUTS.CONTACT_LABEL')],
    ['company', 'name', t('AUDIENCES.FORM.SHORTCUTS.COMPANY')],
    ['deal', 'pipeline_stage_id', t('AUDIENCES.FORM.SHORTCUTS.DEAL_STAGE')],
  ]
    .filter(([entity, attributeKey]) =>
      filterTypesByEntity[entity].value.some(
        type => type.attributeKey === attributeKey
      )
    )
    .map(([entity, attributeKey, label]) => ({ entity, attributeKey, label }))
);

const filledEntities = computed(() =>
  ENTITIES.filter(entity => conditionsOf(entity).length)
);

const addLabel = computed(() =>
  props.direction === 'exclude' && !filledEntities.value.length
    ? t('AUDIENCES.FORM.ADD_EXCLUSION')
    : t('AUDIENCES.FORM.ADD_CONDITION')
);

const isMenuOpen = ref(false);
const opensUpward = ref(false);
const menuAnchorRef = useTemplateRef('menuAnchorRef');

// The scrolling form body clips what overflows it, so open on the side with
// room: upward only when there is not enough below and more above.
const toggleMenu = () => {
  if (!isMenuOpen.value) {
    const anchor = menuAnchorRef.value.getBoundingClientRect();
    const boundary = (
      menuAnchorRef.value.closest('[data-menu-boundary]') ??
      menuAnchorRef.value.closest('dialog')
    )?.getBoundingClientRect();
    const spaceBelow = (boundary?.bottom ?? window.innerHeight) - anchor.bottom;
    const spaceAbove = anchor.top - (boundary?.top ?? 0);
    opensUpward.value = spaceBelow < MENU_HEIGHT_PX && spaceAbove > spaceBelow;
  }
  isMenuOpen.value = !isMenuOpen.value;
};

// Label options feed the "Etiquetas" value pickers. The include block is always
// rendered with the form, so it loads them once for both blocks.
onMounted(() => {
  if (props.direction !== 'include') return;
  useContactLabelsStore().fetchAll();
  useCompanyLabelsStore().fetchAll();
  useDealLabelsStore().fetchAll();
});

const menuSections = computed(() =>
  ENTITIES.map(entity => ({
    title: t(`AUDIENCES.FILTER.${entity.toUpperCase()}.LABEL`),
    items: filterTypesByEntity[entity].value.map(filter => ({
      label: filter.label,
      value: `${entity}:${filter.attributeKey}`,
      action: 'add',
      entity,
      attributeKey: filter.attributeKey,
    })),
  }))
);

// Same reset ConditionRow applies when a condition's field changes, so the
// new row starts with a value shape its input accepts.
const buildCondition = (entity, attributeKey) => {
  const filter = filterTypesByEntity[entity].value.find(
    type => type.attributeKey === attributeKey
  );
  const operator = filter.filterOperators?.[0];
  const inputType = operator?.inputOverride ?? filter.inputType;
  let values = '';
  if (inputType === 'multiSelect') values = [];
  else if (['searchSelect', 'booleanSelect'].includes(inputType)) values = {};

  return {
    attributeKey,
    filterOperator: operator?.value ?? 'equal_to',
    values,
    queryOperator: 'and',
    attributeModel: filter.attributeModel ?? 'standard',
  };
};

// The connector ("E"/"OU") of a new row is the previous condition's query operator, and a saved audience
// does not keep one on its last condition, so default it to "E".
const addCondition = ({ entity, attributeKey }) => {
  const conditions = conditionsOf(entity);
  const previous = conditions[conditions.length - 1];
  if (previous && !previous.queryOperator) previous.queryOperator = 'and';
  conditions.push(buildCondition(entity, attributeKey));
  isMenuOpen.value = false;
};

const editorRefs = useTemplateRef('editorRefs');

const validate = () =>
  (editorRefs.value || []).every(editor => editor.validate());

defineExpose({ validate });
</script>

<template>
  <section class="grid gap-5 p-5 border rounded-lg border-n-weak">
    <header class="grid gap-1.5">
      <h4 class="mb-0 text-sm font-medium text-n-slate-12">{{ title }}</h4>
      <p v-if="description" class="mb-0 text-sm text-n-slate-11">
        {{ description }}
      </p>
    </header>
    <div v-for="entity in filledEntities" :key="entity" class="grid gap-3">
      <span class="text-xs font-medium text-n-slate-11">
        {{ t(`AUDIENCES.FILTER.${entity.toUpperCase()}.LABEL`) }}
      </span>
      <ConditionsEditor
        ref="editorRefs"
        v-model="criteriaByEntity[entity][direction]"
        :filter-types="filterTypesByEntity[entity].value"
      />
    </div>
    <div
      ref="menuAnchorRef"
      v-on-click-outside="() => (isMenuOpen = false)"
      class="relative"
      :class="{ 'pt-4 border-t border-n-weak': filledEntities.length }"
    >
      <button
        v-if="!filledEntities.length"
        type="button"
        class="flex items-center justify-center w-full h-11 gap-2 text-sm border border-dashed rounded-lg border-n-strong text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1"
        @click="toggleMenu"
      >
        <span class="i-lucide-plus size-4" />
        {{ addLabel }}
      </button>
      <Button
        v-else
        type="button"
        sm
        faded
        slate
        icon="i-lucide-plus"
        :label="addLabel"
        @click="toggleMenu"
      />
      <DropdownMenu
        v-if="isMenuOpen"
        show-search
        :search-placeholder="t('AUDIENCES.FORM.SEARCH_FIELD')"
        :menu-sections="menuSections"
        :class="[
          opensUpward ? 'bottom-full mb-1' : 'top-full mt-1',
          filledEntities.length
            ? 'ltr:left-0 rtl:right-0'
            : 'left-1/2 -translate-x-1/2',
        ]"
        class="w-64 max-h-60"
        @action="addCondition"
      />
    </div>
    <div
      v-if="showShortcuts && !filledEntities.length"
      class="flex flex-wrap items-center gap-2"
    >
      <span class="text-xs text-n-slate-11">
        {{ t('AUDIENCES.FORM.SHORTCUTS.TITLE') }}
      </span>
      <Button
        v-for="shortcut in shortcuts"
        :key="shortcut.attributeKey"
        type="button"
        sm
        faded
        slate
        :label="shortcut.label"
        @click="addCondition(shortcut)"
      />
    </div>
  </section>
</template>
